import axios, { type AxiosRequestConfig } from "axios";
import { chain, cloneDeep, sortBy } from "lodash";

import {
  ApolloClient,
  from,
  HttpLink,
  InMemoryCache,
  type ApolloLink,
  type NormalizedCacheObject,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import {
  type AttributionWindowDef,
  enumType,
  formatting,
  type AttributionModel,
  type BaseUserResponse,
  type EcomAccountingSettings,
  type EnumType,
  type UserCurrencies,
  type UserResponse,
} from "@north-beam/nb-common";

import {
  type ApiEnvironment,
  type CreateApolloClientConfig,
  type HeadersParams,
  type UserConfig,
} from "./types";
import { makeHeaders } from "./util";

export type UserClass<U extends AbstractUser<C>, C extends UserConfig> = new (
  config: C,
) => U;

/**
 * An abstract class that represents a user in the application.
 */
export abstract class AbstractUser<C extends UserConfig = UserConfig> {
  private _apolloClient: ApolloClient<NormalizedCacheObject> | null = null;
  private _heavyApolloClient: ApolloClient<NormalizedCacheObject> | null = null;
  private _jobManagerApolloClient: ApolloClient<NormalizedCacheObject> | null =
    null;
  private _cpuIntensiveApolloClient: ApolloClient<NormalizedCacheObject> | null =
    null;
  /**
   * The response associated with current user to display, regardless of whether the authedUser has
   * the right access to view. This is determined by looking at the URL path. If the URL path is
   * empty, then it is determined by the matrix below. Otherwise, it is the user whose ID is
   * referenced in the URL path.
   *
   *                            |     Activated | Not Activated |
   *    ------------------------+---------------+---------------+
   *     Has defaultDisplayUser |   No redirect |  REDIRECT (*) |
   *    ------------------------+---------------+---------------+
   *     No  defaultDisplayUser |   No redirect |   No redirect |
   *    ------------------------+---------------+---------------+
   *
   * (*) Redirect only takes place if the defaultDisplayUser != displayUser.
   *
   * The defaultDisplayUser refers to the user in which the current page will redirect to if the
   * displayUser isn't activated.  If it is the user associated with the `default_display_user_id`
   * field for the displayUser, if it exists. Otherwise, it is null.
   *
   * The redirect will update the URL path to include the ID of the defaultDisplayUser, thus
   * effectively changing the displayUser.
   *
   * After the redirect, we'll use the matrix above (with the new displayUser) to determine whether
   * we need to redirect a second time.
   */
  private displayUserResponse: BaseUserResponse;
  /**
   * The response associated with the user whose Auth0 credentials we are using to currently serve
   * Northbeam.
   */
  private readonly authedUserResponse: UserResponse;
  public readonly email: string;
  protected readonly getTokenSilently: () => Promise<string | undefined>;
  protected readonly env: ApiEnvironment;
  private readonly actingUserId?: string;
  public readonly isTrueAccountOwner?: boolean;
  public readonly attributionWindows: AttributionWindowDef[];
  public readonly attributionModels: AttributionModel[];
  public readonly attributionMethodEnum: EnumType<string, any>;
  public readonly featureFlags: Record<string, boolean>;

  constructor(params: C) {
    this.email = params.email;
    this.authedUserResponse = params.authedUserResponse;
    this.env = params.env;
    this.displayUserResponse =
      params.authedUserResponse.impersonatedUser ?? params.authedUserResponse;
    this.getTokenSilently = params.getTokenSilently;
    this.isTrueAccountOwner =
      params.authedUserResponse.isTrueAccountOwner ?? false;
    this.actingUserId = params.actingUserId;
    this.attributionWindows = cloneDeep(this.authedUser.attributionWindows);

    const models = cloneDeep(this.authedUser.attributionModels);

    this.attributionModels = sortBy(
      models?.map((m) => ({
        ...m,
        name:
          m.id === "northbeam_custom__va"
            ? m.name.replace("and", "+").replace("views", "Views")
            : m.name,
      })),
      "name",
    );

    const enumObject = chain(this.attributionModels)
      .map(({ id }) => [id, id])
      .fromPairs()
      .value();
    const labels = chain(this.attributionModels)
      .map(({ id, name, isAdminOnly }) => {
        const modelName = formatting.formatAttributionModel(name);
        const adminInfo = isAdminOnly ? " - Admin Only" : "";
        return [id, `${modelName}${adminInfo}`];
      })
      .fromPairs()
      .value();
    const keyOrder = this.attributionModels.map((v) => v.id);

    this.attributionMethodEnum = enumType({
      enumObject,
      labels,
      keyOrder,
    });

    this.featureFlags = cloneDeep(this.authedUserResponse.featureFlags);
  }

  pathFromRoot(target: string): string {
    const prefix = `/${this.displayUserId}`;
    if (target.startsWith("/")) {
      return prefix + target;
    }
    return prefix + "/" + target;
  }

  get displayUserId(): string {
    return this.displayUserResponse.id;
  }

  get activatedAt(): string | null {
    return this.displayUserResponse.activatedAt;
  }

  get deactivatedAt(): string | null {
    return this.displayUserResponse.deactivatedAt;
  }

  get timezone(): string {
    return this.displayUserResponse.timezone;
  }

  get firstName() {
    return this.displayUserResponse.firstName;
  }

  get lastName() {
    return this.displayUserResponse.lastName;
  }

  get clientSettings() {
    return this.displayUserResponse.clientSettings;
  }

  get isPixelConfigurationSubaccount() {
    return this.displayUserResponse.isPixelConfigurationSubaccount ?? false;
  }

  get axios() {
    return axios.create({
      baseURL: this.env.apiRoot,
      withCredentials: true,
    });
  }

  get authedUser() {
    return this.authedUserResponse;
  }

  updateDisplayUserResponse(update: Partial<BaseUserResponse>) {
    this.displayUserResponse = { ...this.displayUserResponse, ...update };
  }

  getDefaultAttributionModel(
    value: string | null | undefined,
    amEnum: EnumType<string, any> | undefined = undefined,
  ) {
    amEnum = amEnum ?? this.attributionMethodEnum;

    let dv =
      amEnum.fromString(value) ??
      this.clientSettings.defaultDisplayAttributionModel ??
      "northbeam_custom";
    const keyOrder: string[] = amEnum.keyOrder;

    if (!keyOrder.includes(dv)) {
      dv = "last_touch";
    }

    return dv;
  }

  get currencies(): UserCurrencies {
    return this.displayUserResponse.currencies;
  }

  get isDemoMode() {
    return !!this.featureFlags?.["demoMode"];
  }
  get useDevelopmentAnalyticsData() {
    return !!this.featureFlags?.["useDevelopmentAnalyticsData"];
  }

  get companyName(): string | null {
    if (this.isDemoMode) {
      return "Widgets Co";
    }
    return this.displayUserResponse.companyName ?? null;
  }

  get authedUserId(): string {
    return this.authedUserResponse.id;
  }

  get defaultDisplayUserId(): string | null {
    return this.displayUserResponse.defaultDisplayUserId ?? null;
  }

  get isAdmin(): boolean {
    return this.authedUserResponse.isAdmin;
  }

  get ecomAccountingSettings(): EcomAccountingSettings {
    return this.displayUserResponse.ecomAccountingSettings;
  }

  get headerParams(): HeadersParams {
    const customBqDataset = localStorage.getItem("developmentDataset");
    return {
      getTokenSilently: this.getTokenSilently,
      impersonateUserId: this.impersonatedUser?.id || "",
      isDemoMode: this.isDemoMode,
      useDevelopmentAnalyticsData: this.useDevelopmentAnalyticsData,
      actingUserId: this.actingUserId,
      customBqDataset: customBqDataset
        ? JSON.parse(customBqDataset)
        : undefined,
    };
  }

  async getHeaders() {
    return await makeHeaders(this.headerParams);
  }

  get impersonatedUser(): BaseUserResponse | undefined {
    return this.authedUserResponse.impersonatedUser;
  }

  get apolloClient(): ApolloClient<NormalizedCacheObject> {
    if (!this._apolloClient) {
      this._apolloClient = this.createApolloClient();
    }
    if (!this._apolloClient) {
      throw new Error("Apollo client is not initialized!");
    }
    return this._apolloClient;
  }

  get heavyApolloClient(): ApolloClient<NormalizedCacheObject> {
    if (!this._heavyApolloClient) {
      this._heavyApolloClient = this.createApolloClient({ heavy: true });
    }
    if (!this._heavyApolloClient) {
      throw new Error("Apollo client is not initialized!");
    }
    return this._heavyApolloClient;
  }

  get cpuIntensiveApolloClient(): ApolloClient<NormalizedCacheObject> {
    if (!this._cpuIntensiveApolloClient) {
      this._cpuIntensiveApolloClient = this.createApolloClient({
        cpuIntensive: true,
      });
    }
    if (!this._cpuIntensiveApolloClient) {
      throw new Error("Apollo client is not initialized!");
    }
    return this._cpuIntensiveApolloClient;
  }

  get jobManagerApolloClient(): ApolloClient<NormalizedCacheObject> {
    if (!this._jobManagerApolloClient) {
      this._jobManagerApolloClient = this.createApolloClient({
        jobManager: true,
      });
    }
    if (!this._jobManagerApolloClient) {
      throw new Error("Apollo client is not initialized!");
    }
    return this._jobManagerApolloClient;
  }

  private createApolloClient = (
    options?: CreateApolloClientConfig,
  ): ApolloClient<NormalizedCacheObject> => {
    const {
      heavy = false,
      cpuIntensive = false,
      jobManager = false,
    } = options ?? {};
    const abortController = new AbortController();
    const setHeaders: ApolloLink = setContext(async () => {
      const headers = await this.getHeaders();

      return { headers };
    });

    const errorLink = onError(({ graphQLErrors, operation, forward }) => {
      if (graphQLErrors) {
        for (const def of operation.query.definitions) {
          if (!("operation" in def) || def.operation === "mutation") {
            return;
          }
        }
        return forward(operation);
      }
    });

    let endpoint: string;
    if (heavy && this.env.heavyApiRoot) {
      endpoint = this.env.heavyApiRoot;
    } else if (cpuIntensive && this.env.cpuIntensiveApiRoot) {
      endpoint = this.env.cpuIntensiveApiRoot;
    } else if (jobManager && this.env.jobManagerApiRoot) {
      endpoint = this.env.jobManagerApiRoot;
    } else {
      endpoint = this.env.apiRoot;
    }

    return new ApolloClient({
      cache: new InMemoryCache({
        typePolicies: {
          OverviewPage: {
            keyFields: [],
          },
          User: {
            keyFields: [],
            fields: {
              mmm: {
                merge(existing, incoming, { mergeObjects }) {
                  return mergeObjects(existing, incoming);
                },
              },
              pixelTrackingEvents: {
                merge(existing, incoming, { mergeObjects }) {
                  return mergeObjects(existing, incoming);
                },
              },
            },
          },
          benchmarks: {
            keyFields: [],
          },
        },
      }),
      // This is what enables cancelation
      queryDeduplication: false,
      link: from([
        setHeaders,
        new RetryLink(),
        errorLink,
        new HttpLink({
          uri: `${endpoint}api/graphql`,
          fetchOptions: {
            mode: "cors",
            signal: abortController.signal,
          },
        }),
      ]),
    });
  };

  async callApi<T>(
    url: string,
    { headers: _headers, ...config }: Omit<AxiosRequestConfig, "url">,
  ): Promise<T> {
    const result = await this.axios.request<T>({
      ...config,
      url,
      headers: {
        ..._headers,
        ...(await this.getHeaders()),
      },
    });
    return result.data;
  }
}
